import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  attachGdPositionAttachmentFromProjectFoto,
  deleteGdPositionAttachment,
  getGdPositionAttachmentThumbUrl,
  getGdPositionAttachmentUrl,
  listGdPositionAttachments,
  uploadGdPositionAttachment,
  type GdPositionAttachmentMeta,
} from "@/lib/dokumente";
import { imageElementFromContextEvent } from "@/lib/clipboardMedia";
import type { ProjectFoto } from "@/lib/projectOverview";
import { Lightbox } from "@/pages/material/Lightbox";
import { useMaMediaContextMenu } from "@/pages/material/useMaMediaContextMenu";
import { getPositionMeta, type EditorPosition } from "@/pages/dokumente/documentPositionModel";
import { DocumentPositionProjectFotoPicker } from "@/pages/dokumente/DocumentPositionProjectFotoPicker";
import { DOCUMENT_EDITOR_NESTED_OVERLAY_Z } from "@/pages/dokumente/documentEditorLayers";

interface DocumentPositionImagesProps {
  docId: number | null;
  auftragId?: number | null;
  position: EditorPosition;
  editable: boolean;
  onEnsureMediaKey: (position: EditorPosition) => EditorPosition;
  onAttachmentsChanged?: () => void;
  zIndexClass?: string;
}

export function DocumentPositionImages({
  docId,
  auftragId = null,
  position,
  editable,
  onEnsureMediaKey,
  onAttachmentsChanged,
  zIndexClass,
}: DocumentPositionImagesProps) {
  const [items, setItems] = useState<GdPositionAttachmentMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [lightbox, setLightbox] = useState<{
    url: string;
    filename: string;
    contentType?: string | null;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { openMediaContextMenu, contextMenuNode } = useMaMediaContextMenu();

  const mediaKey = (getPositionMeta(position).media_key || "").trim();
  const positionId = position.id ?? null;
  const canPickFromProject = Boolean(auftragId && Number(auftragId) > 0);

  const filtered = useMemo(
    () =>
      items.filter(
        (item) =>
          (mediaKey && item.media_key === mediaKey) ||
          (positionId != null && item.position_id === positionId),
      ),
    [items, mediaKey, positionId],
  );

  const reload = useCallback(async () => {
    if (!docId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await listGdPositionAttachments(docId);
      setItems(list);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Bilder konnten nicht geladen werden.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [docId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  function notifyChanged() {
    onAttachmentsChanged?.();
  }

  async function ensureKey(): Promise<{ key: string; positionId: number | null } | null> {
    const withKey = onEnsureMediaKey(position);
    const key = (getPositionMeta(withKey).media_key || "").trim();
    if (!key) {
      setError("Bildschlüssel konnte nicht erzeugt werden. Bitte Position speichern und erneut versuchen.");
      return null;
    }
    return { key, positionId: withKey.id ?? null };
  }

  async function handleUpload(files: FileList | null) {
    if (!docId || !files?.length || !editable) return;
    const ensured = await ensureKey();
    if (!ensured) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        await uploadGdPositionAttachment(docId, {
          mediaKey: ensured.key,
          positionId: ensured.positionId,
          file,
        });
      }
      await reload();
      notifyChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function handleAttachFromProject(fotos: ProjectFoto[]) {
    if (!docId || !editable || !fotos.length) return;
    const ensured = await ensureKey();
    if (!ensured) throw new Error("Bildschlüssel fehlt.");
    setUploading(true);
    setError(null);
    try {
      for (const foto of fotos) {
        await attachGdPositionAttachmentFromProjectFoto(docId, {
          mediaKey: ensured.key,
          positionId: ensured.positionId,
          fotoId: foto.id,
        });
      }
      await reload();
      notifyChanged();
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(id: number) {
    if (!docId || !editable) return;
    setError(null);
    try {
      await deleteGdPositionAttachment(docId, id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      notifyChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  if (!docId) {
    return (
      <p className="text-sm text-label-secondary">
        Speichere das Dokument einmal, bevor du Bilder zu Positionen hinzufügst.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-label">Bilder</p>
          <p className="text-xs text-label-secondary">
            Vom PC, aus dem Projekt oder per QR vom Handy – sichtbar im Angebot und im
            Projekt-SharePoint.
          </p>
        </div>
        {editable ? (
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => void handleUpload(e.target.files)}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              loading={uploading}
              onClick={() => inputRef.current?.click()}
            >
              Bilder vom PC
            </Button>
            {canPickFromProject ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={uploading}
                onClick={() => setProjectPickerOpen(true)}
              >
                Aus dem Projekt
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner className="h-4 w-4" />
          Bilder werden geladen …
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-label-secondary">Noch keine Bilder zu dieser Position.</p>
      ) : (
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {filtered.map((item) => {
            const fullUrl = getGdPositionAttachmentUrl(docId, item.id);
            const thumbUrl = getGdPositionAttachmentThumbUrl(docId, item.id);
            const contentType = item.content_type || "image/jpeg";
            return (
              <li
                key={item.id}
                className="overflow-hidden rounded-lg border border-separator bg-card"
              >
                <button
                  type="button"
                  className="block w-full overflow-hidden bg-fill-tertiary"
                  onClick={() =>
                    setLightbox({
                      url: thumbUrl,
                      filename: item.filename || "bild.jpg",
                      contentType,
                    })
                  }
                  onContextMenu={(e) =>
                    openMediaContextMenu(e, {
                      url: fullUrl,
                      filename: item.filename || "bild.jpg",
                      contentType,
                      isImage: true,
                      imageElement: imageElementFromContextEvent(e.target, e.currentTarget),
                    })
                  }
                >
                  <img
                    src={thumbUrl}
                    alt={item.filename}
                    crossOrigin="use-credentials"
                    className="h-28 w-full object-cover"
                  />
                </button>
                <div className="flex items-center justify-between gap-1 px-2 py-1.5">
                  <span className="truncate text-xs text-label-secondary">{item.filename}</span>
                  {editable ? (
                    <button
                      type="button"
                      className="text-xs font-medium text-status-red hover:underline"
                      onClick={() => void handleDelete(item.id)}
                    >
                      Löschen
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Lightbox
        url={lightbox?.url ?? null}
        copySource={
          lightbox
            ? {
                url: lightbox.url,
                filename: lightbox.filename,
                contentType: lightbox.contentType || "image/jpeg",
              }
            : null
        }
        onClose={() => setLightbox(null)}
        zIndexClass="z-[320]"
      />
      {contextMenuNode}

      {canPickFromProject && auftragId ? (
        <DocumentPositionProjectFotoPicker
          open={projectPickerOpen}
          auftragId={Number(auftragId)}
          onClose={() => setProjectPickerOpen(false)}
          onConfirm={handleAttachFromProject}
          zIndexClass={zIndexClass ? DOCUMENT_EDITOR_NESTED_OVERLAY_Z : undefined}
        />
      ) : null}
    </div>
  );
}
